import { Fade } from '@heidi/design-system/fade';
import { useVerticalScrollFade } from '@heidi/design-system/hooks/use-vertical-scroll-fade';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import {
  AlertCircle,
  Ban,
  Check,
  Circle,
  CircleDot,
  ListTodo,
  Minus,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { agentTodoMessages } from '../../../../data/projects/timeline/agent-todo/agentTodo.messages';
import { findCurrentTodoItemId } from '../../../../data/projects/timeline/agent-todo/mergeTodoItemStatuses';
import type {
  AgentTodoItem,
  AgentTodoItemStatus,
} from '../../../../data/projects/timeline/agent-todo/types';
import { ShimmerText } from '../ShimmerText';

/** Viewport fits ~5 rows; longer lists scroll and open pinned to the current item. */
const LIST_VIEWPORT_MAX_HEIGHT = 'max-h-[260px]';

const ATOM_ANIMATE_IN =
  'motion-safe:duration-300 motion-safe:ease-out-quart motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1';

type TodoRowDisplay = 'done' | 'current' | 'future';

interface AgentTodoResultBlockProps {
  status: 'running' | 'completed' | 'failed';
  title: string;
  subtitle?: string;
  items: AgentTodoItem[];
  error?: string;
  className?: string;
}

const rowDisplayForItem = (
  item: AgentTodoItem,
  currentItemId: number | null
): TodoRowDisplay => {
  if (item.status === 'completed' || item.status === 'cancelled') return 'done';
  if (item.id === currentItemId) return 'current';
  return 'future';
};

const StatusIcon = ({
  status,
  display,
}: {
  status: AgentTodoItemStatus;
  display: TodoRowDisplay;
}) => {
  if (display === 'done') {
    if (status === 'completed') {
      return <Check className="size-4 text-validation-success" aria-hidden />;
    }
    return <Minus className="size-4 text-text-tertiary" aria-hidden />;
  }

  if (display === 'current') {
    if (status === 'in_progress') {
      return <CircleDot className="size-4 text-text-primary" aria-hidden />;
    }
    return <Circle className="size-4 text-text-primary" aria-hidden />;
  }

  return <Circle className="size-4 text-text-tertiary" aria-hidden />;
};

/**
 * Read-only checklist for the harness `todo` tool. Mirrors the clinical-task
 * result block layout without interactive toggles — agent todos are orchestration
 * scratchpad state, not persisted clinical tasks.
 */
export const AgentTodoResultBlock = ({
  status,
  title,
  subtitle,
  items,
  error,
  className,
}: AgentTodoResultBlockProps) => {
  const intl = useIntl();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  const handleToggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);
  const listExpanded = !isCollapsed && items.length > 0;
  const { canScrollDown } = useVerticalScrollFade(listRef, listExpanded, [
    items,
    isCollapsed,
  ]);
  const currentItemId = status === 'completed' ? findCurrentTodoItemId(items) : null;

  useEffect(() => {
    if (status !== 'completed' || isCollapsed || currentItemId === null) return;
    const row = listRef.current?.querySelector(`[data-todo-id="${currentItemId}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [status, isCollapsed, currentItemId, items]);

  if (status === 'running') {
    return (
      <div
        className={cn(
          'flex w-full min-w-0 items-center gap-2',
          ATOM_ANIMATE_IN,
          className
        )}
        data-testid="agent-todo-block"
        data-status="running"
      >
        <ListTodo className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
        <ShimmerText className="min-w-0 flex-1 text-sm leading-normal text-text-tertiary">
          {title}
        </ShimmerText>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div
        className={cn('flex w-full min-w-0 flex-col gap-0.5', className)}
        data-testid="agent-todo-block"
        data-status="failed"
      >
        <div className="flex min-w-0 items-center gap-2">
          <AlertCircle
            className="size-3.5 shrink-0 text-validation-destructive"
            aria-hidden
          />
          <TypographyP2 className="text-sm text-validation-destructive">
            {title}
          </TypographyP2>
        </div>
        {error ? (
          <TypographyCaption className="ps-5 text-text-tertiary">
            {error}
          </TypographyCaption>
        ) : null}
      </div>
    );
  }

  const completedCount = items.filter((item) => item.status === 'completed').length;
  const showCollapse = items.length > 0;

  const headerRow = (
    <>
      <ListTodo className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
      <TypographyP2 className="min-w-0 flex-1 text-sm leading-normal text-text-tertiary">
        {title}
      </TypographyP2>
    </>
  );

  const expandedFooterCaption =
    subtitle ??
    (items.length > 1
      ? intl.formatMessage(agentTodoMessages.completedProgress, {
          completed: completedCount,
          total: items.length,
        })
      : undefined);

  return (
    <div
      className={cn('flex w-full flex-col gap-2', className)}
      data-testid="agent-todo-block"
      data-status="completed"
      data-item-count={items.length}
    >
      {showCollapse ? (
        <button
          type="button"
          onClick={handleToggleCollapse}
          aria-expanded={!isCollapsed}
          className={cn(
            'flex w-full min-w-0 items-center gap-2 text-start',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus'
          )}
        >
          {headerRow}
        </button>
      ) : (
        <div className="flex w-full min-w-0 items-center gap-2 p-0.5">{headerRow}</div>
      )}

      {listExpanded ? (
        <div className="flex flex-col gap-2">
          <div className="relative [--fade-bg:hsl(var(--twc-surface-container))]">
            <div
              ref={listRef}
              className={cn(
                'scrollbar-fade flex flex-col gap-2 overflow-y-auto',
                LIST_VIEWPORT_MAX_HEIGHT
              )}
            >
              {items.map((item) => {
                const display = rowDisplayForItem(item, currentItemId);
                return (
                  <div
                    key={item.id}
                    data-todo-id={item.id}
                    data-todo-display={display}
                    className={cn(
                      'flex items-start gap-2 rounded-lg border border-border bg-surface p-2',
                      display === 'future' && 'opacity-80',
                      display === 'done' && 'opacity-70'
                    )}
                  >
                    <div className="mt-0.5 shrink-0">
                      <StatusIcon status={item.status} display={display} />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <TypographyP2
                        className={cn(
                          display === 'current' && 'font-medium text-text-primary',
                          display === 'future' && 'text-text-secondary',
                          display === 'done' &&
                            (item.status === 'completed'
                              ? 'text-text-secondary line-through'
                              : 'text-text-tertiary line-through')
                        )}
                      >
                        {item.title}
                      </TypographyP2>
                      {item.notes ? (
                        <TypographyCaption
                          className={cn(
                            display === 'current'
                              ? 'text-text-secondary'
                              : 'text-text-tertiary'
                          )}
                        >
                          {item.notes}
                        </TypographyCaption>
                      ) : null}
                    </div>
                    {item.status === 'cancelled' ? (
                      <Ban
                        className="mt-0.5 size-4 shrink-0 text-text-tertiary"
                        aria-hidden
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
            {canScrollDown ? (
              <Fade
                side="bottom"
                background="var(--fade-bg)"
                blur="4px"
                className="inset-x-0 bottom-0 z-10 h-10"
                data-testid="agent-todo-list-bottom-fade"
              />
            ) : null}
          </div>
          {expandedFooterCaption ? (
            <TypographyCaption className="text-end text-text-tertiary">
              {expandedFooterCaption}
            </TypographyCaption>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
